import html2pdf from 'html2pdf.js';

const rewriteToWsrv = (rawUrl: string) =>
  `https://wsrv.nl/?url=${encodeURIComponent(rawUrl)}`;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const rewriteToProxy = (rawUrl: string) => {
  const base = API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base.replace(/\/$/, '')}/image-proxy?url=${encodeURIComponent(rawUrl)}`;
};

const proxyHosts = new Set([
  'api.tbotechnology.in',
  'www.tboholidays.com',
  'tboholidays.com',
]);

const isPrivateHost = (host: string) => {
  const lower = host.toLowerCase();
  if (lower === 'localhost' || lower.endsWith('.local')) return true;
  if (/^127\./.test(lower)) return true;
  if (/^10\./.test(lower)) return true;
  if (/^192\.168\./.test(lower)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(lower)) return true;
  return false;
};

const imagePreloadCache = new Map<string, Promise<boolean>>();

export function resetPdfImageCacheForTests() {
  imagePreloadCache.clear();
}

function collectImageUrls(root: HTMLElement) {
  return Array.from(root.querySelectorAll<HTMLImageElement>('img'))
    .map((img) => img.currentSrc || img.src)
    .map((src) => String(src || '').trim())
    .filter((src) => Boolean(src) && !src.startsWith('data:') && !src.startsWith('blob:'));
}

function waitForSettledImage(img: HTMLImageElement, timeoutMs = 8000) {
  return new Promise<void>((resolve) => {
    if (img.complete) {
      return resolve();
    }

    const onDone = () => {
      img.removeEventListener('load', onDone);
      img.removeEventListener('error', onDone);
      resolve();
    };

    img.addEventListener('load', onDone, { once: true });
    img.addEventListener('error', onDone, { once: true });
    setTimeout(onDone, timeoutMs);
  });
}

function preloadImage(url: string) {
  const cached = imagePreloadCache.get(url);
  if (cached) return cached;

  const promise = new Promise<boolean>((resolve) => {
    if (typeof Image === 'undefined') {
      resolve(false);
      return;
    }

    const preload = new Image();
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const finish = (success: boolean) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      resolve(success);
    };

    preload.onload = () => finish(true);
    preload.onerror = () => finish(false);
    preload.crossOrigin = 'anonymous';
    preload.decoding = 'async';

    timeoutId = setTimeout(() => finish(false), 12000);

    try {
      preload.src = url;
      if (preload.complete && preload.naturalWidth > 0) {
        finish(true);
      }
    } catch {
      finish(false);
    }
  });

  imagePreloadCache.set(url, promise);
  promise.then((success) => {
    if (!success) {
      imagePreloadCache.delete(url);
    }
  });
  return promise;
}

export async function preloadPdfImages(root: HTMLElement) {
  const urls = Array.from(new Set(collectImageUrls(root)));
  await Promise.all(urls.map((url) => preloadImage(url)));
}

export async function waitForPdfImages(root: HTMLElement, timeoutMsPerImage = 8000) {
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  await Promise.all([
    preloadPdfImages(root),
    ...imgs.map((img) => waitForSettledImage(img, timeoutMsPerImage)),
  ]);
}

async function inlineSameOriginSvgs(root: HTMLElement) {
  const restored = new Map<HTMLImageElement, { src: string; srcset: string }>();
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  for (const img of imgs) {
    const originalSrc = img.currentSrc || img.src;
    if (!originalSrc || originalSrc.startsWith('data:') || originalSrc.startsWith('blob:')) continue;
    try {
      /* eslint-disable no-restricted-globals */
      const u = new URL(originalSrc, location.href);
      if (u.origin !== location.origin) continue;
      if (!u.pathname.toLowerCase().endsWith('.svg')) continue;

      const res = await fetch(u.toString(), { credentials: 'same-origin' });
      if (!res.ok) continue;
      const svgText = await res.text();
      if (!svgText) continue;

      restored.set(img, { src: img.src, srcset: img.srcset });
      img.crossOrigin = 'anonymous';
      img.loading = 'eager';
      img.srcset = '';
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
    } catch {
      // ignore
    }
  }

  return () => {
    for (const [img, { src, srcset }] of Array.from(restored.entries())) {
      img.srcset = srcset;
      img.src = src;
    }
  };
}

function rewriteExternalImages(root: HTMLElement) {
  const restored = new Map<HTMLImageElement, { src: string; srcset: string }>();
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));

  for (const img of imgs) {
    const originalSrc = img.currentSrc || img.src;
    if (!originalSrc || originalSrc.startsWith('data:') || originalSrc.startsWith('blob:')) continue;

    try {
      /* eslint-disable no-restricted-globals */
      const u = new URL(originalSrc, location.href);
      const hostname = u.hostname.toLowerCase();
      if (proxyHosts.has(hostname)) {
        restored.set(img, { src: img.src, srcset: img.srcset });
        img.crossOrigin = 'anonymous';
        img.loading = 'eager';
        img.srcset = '';
        img.src = rewriteToProxy(originalSrc);
        continue;
      }

      if (u.origin === location.origin || isPrivateHost(hostname)) {
        img.crossOrigin = 'anonymous';
        img.loading = 'eager';
        continue;
      }

      restored.set(img, { src: img.src, srcset: img.srcset });
      img.crossOrigin = 'anonymous';
      img.loading = 'eager';
      img.srcset = '';
      img.src = rewriteToWsrv(originalSrc);
    } catch {
    }
  }

  return () => {
    for (const [img, { src, srcset }] of Array.from(restored.entries())) {
      img.srcset = srcset;
      img.src = src;
    }
  };
}

export async function generatePdf(element: HTMLElement, filename: string) {
  const restore = rewriteExternalImages(element);
  const restoreSvgs = await inlineSameOriginSvgs(element);
  try {
    await waitForPdfImages(element);
    await html2pdf()
      .set({
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['.pdf-keep-together', '.pdf-inclusion-card-wrapper', '.inclusion-section-heading'],
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: '#ffffff',
        },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
      })
      .from(element)
      .save();
  } finally {
    restoreSvgs();
    restore();
  }
}

export async function generatePdfBlob(element: HTMLElement, filename: string): Promise<Blob> {
  const restore = rewriteExternalImages(element);
  const restoreSvgs = await inlineSameOriginSvgs(element);
  try {
    await waitForPdfImages(element);
    const worker = html2pdf()
      .set({
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['.pdf-keep-together', '.pdf-inclusion-card-wrapper', '.inclusion-section-heading'],
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: '#ffffff',
        },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
      })
      .from(element)
      .toPdf();

    const pdf: any = await (worker as any).get('pdf');
    return pdf.output('blob');
  } finally {
    restoreSvgs();
    restore();
  }
}

export function downloadJson(data: any, filename: string) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
